/**
 * Subscription prep toasts: freemium daily open, first crossing nominal freemium cap/day (limits still off by default).
 * Gated by native shell + IAP product IDs configured + AI_SUBSCRIPTION_TOASTS_ENABLED.
 */

import { toast } from 'sonner';
import {
  AI_SUBSCRIPTION_TOASTS_ENABLED,
  AI_TOKENS_FREEMIUM_PER_DAY,
  getAiUsageTierForUid,
  shouldRunSubscriptionTierNudges,
} from './aiTokenEntitlements';
import { requiresNativeSubscriptionGate } from './nativeSubscriptions';
import { addAiTokenUsage, formatDate, getAiTokenUsageDay } from './storage';

const formatYmd = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

function canNudge(uid) {
  if (!AI_SUBSCRIPTION_TOASTS_ENABLED || !uid) return false;
  if (!shouldRunSubscriptionTierNudges()) return false;
  if (!requiresNativeSubscriptionGate()) return false;
  return true;
}

/** Once per local calendar day when freemium user opens app (trial ended). */
export function runFreemiumOpenSubscriptionNudge(uid) {
  if (!canNudge(uid)) return;
  if (getAiUsageTierForUid(uid) !== 'freemium') return;
  const today = formatYmd(new Date());
  const key = `rbm_toast_freemium_daily_${uid}_${today}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    return;
  }
  toast.message(
    'Subscribe for full daily AI (250k tokens/day). The free tier includes 10k/day after your first week.',
  );
}

/**
 * When estimated usage first reaches freemium nominal cap in a day (soft launch: toast only).
 * @param {number} prevUsed
 * @param {number} newUsed
 */
export function maybeToastFreemiumTokenThreshold(uid, prevUsed, newUsed) {
  if (!canNudge(uid)) return;
  if (getAiUsageTierForUid(uid) !== 'freemium') return;
  const prev = Math.max(0, Number(prevUsed) || 0);
  const next = Math.max(0, Number(newUsed) || 0);
  if (next < AI_TOKENS_FREEMIUM_PER_DAY) return;
  if (prev >= AI_TOKENS_FREEMIUM_PER_DAY) return;
  const today = formatYmd(new Date());
  const key = `rbm_toast_10k_cross_${uid}_${today}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    return;
  }
  toast.message(
    "You've reached today's free AI estimate (10k). Subscribe for 250k/day, or add a 30-day unlimited pass when it's available in the store.",
  );
}

/** Same as addAiTokenUsage plus optional freemium 10k crossing toast (native + IAP configured). */
export function addAiTokenUsageWithSubscriptionNudge(
  uid,
  estimatedTokens,
  todayStr = formatDate(new Date()),
  opts = {},
) {
  if (opts?.skipAccounting) {
    return getAiTokenUsageDay();
  }
  const before = getAiTokenUsageDay();
  const prevUsed = before.date === todayStr ? before.used : 0;
  const r = addAiTokenUsage(estimatedTokens, todayStr);
  maybeToastFreemiumTokenThreshold(uid, prevUsed, r.used);
  return r;
}
